import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import logo from '../assets/turet-logo.jpg';

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Tıklama dışı algılama ile menüyü kapatma
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('nav')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const navLinks = [
        { name: 'Ana Sayfa', target: 'home' },
        {
            name: 'Hakkımızda',
            // target: 'hakkımızda', // Dropdown başlığı artık, link değil
            hasDropdown: true,
            subItems: [
                { name: 'Topluluk Koordinatörümüzün Mesajı', target: '/koordinator', isRoute: true },
                { name: 'Vizyonumuz', target: '/vizyon', isRoute: true },
                { name: 'Misyonumuz', target: '/misyon', isRoute: true },
                { name: 'Logomuz Hakkında', target: '/logomuz', isRoute: true }
            ]
        },
        { name: 'Etkinlikler', target: '/etkinlikler', isRoute: true },
        { name: 'Duyurular', target: '/duyurular', isRoute: true },
        {
            name: 'Ekip',
            hasDropdown: true,
            subItems: [
                { name: 'Topluluk Başkanı', target: '/ekip/baskan', isRoute: true },
                { name: 'Yönetim Kurulu', target: '/ekip/yonetim', isRoute: true },
                { name: 'Denetim Kurulu', target: '/ekip/denetim', isRoute: true }
            ]
        },
        {
            name: 'Blog',
            hasDropdown: true,
            subItems: [
                { name: 'Köşe Yazıları', target: '/kose-yazilari', isRoute: true },
                { name: 'Dergimiz', target: '/dergimiz', isRoute: true }
            ]
        },
        {
            name: 'Medya',
            hasDropdown: true,
            subItems: [
                { name: 'Sosyal Medya', target: '/medya/sosyal', isRoute: true },
                { name: 'Basında Biz', target: '/medya/basin', isRoute: true }
            ]
        },
        { name: 'İletişim', target: '/iletisim', isRoute: true }
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavigation = (target, isRoute = false) => {
        setActiveDropdown(null); // Menüyü kapat
        if (isRoute) {
            navigate(target);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (target === 'home') {
            navigate('/');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            if (location.pathname !== '/') {
                navigate('/');
                setTimeout(() => {
                    const element = document.getElementById(target);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            } else {
                const element = document.getElementById(target);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    };

    return (
        <nav
            className="fixed top-0 left-0 right-0 z-50 font-sans shadow-lg bg-blue-900/50 backdrop-blur-md py-4 transition-all duration-300"
        >
            <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                {/* Logo Section */}
                <button onClick={() => handleNavigation('home')} className="flex items-center gap-4 text-left group">
                    <img
                        src={logo}
                        alt="TURET Logo"
                        className="w-14 h-14 rounded-full object-cover border-2 border-sand-beige shadow-md group-hover:border-vibrant-orange transition-colors duration-300"
                    />
                    <div className="flex flex-col">
                        <span className="font-bold text-lg leading-none tracking-wide text-white group-hover:text-white/90 transition-colors duration-300">
                            MSKÜ TURET
                        </span>
                        <span className="font-medium text-[0.6rem] md:text-[0.65rem] tracking-wider leading-tight mt-1 text-white/90">
                            TURİST REHBERLİĞİ TOPLULUĞU
                        </span>
                    </div>
                </button>

                {/* Navigation Links */}
                <div className="hidden lg:flex items-center gap-6">
                    {navLinks.map((link) => {
                        // Check if any subItem is active
                        const isSubItemActive = link.subItems?.some(subItem => location.pathname === subItem.target);
                        const isMainItemActive = link.isRoute ? location.pathname === link.target : false;
                        const isActive = isSubItemActive || isMainItemActive || activeDropdown === link.name;

                        return (
                            <div key={link.name} className="relative">
                                <button
                                    onClick={(e) => {
                                        if (link.hasDropdown) {
                                            e.stopPropagation();
                                            setActiveDropdown(activeDropdown === link.name ? null : link.name);
                                        } else {
                                            handleNavigation(link.target, link.isRoute);
                                        }
                                    }}
                                    className={`flex items-center gap-1 text-base font-extrabold drop-shadow-md px-4 py-2 rounded-full hover:bg-white hover:text-cyan-700 hover:shadow-lg hover:scale-105 transition-all duration-300 ${isActive ? 'bg-white text-cyan-700 scale-105' : 'text-white'}`}
                                >
                                    {link.name}
                                    {link.hasDropdown && (
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                                    )}
                                </button>

                                {/* Click-based Dropdown */}
                                {link.hasDropdown && (
                                    <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 transform origin-top w-56 ${activeDropdown === link.name ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`}>
                                        <div className="bg-slate-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden py-2 ring-1 ring-black/20">
                                            {link.subItems.map((subItem) => (
                                                <button
                                                    key={subItem.name}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleNavigation(subItem.target, subItem.isRoute);
                                                    }}
                                                    className={`block w-full text-left px-6 py-3 text-sm font-bold transition-colors border-l-4 border-transparent hover:border-cyan-400 ${location.pathname === subItem.target ? 'bg-white/20 text-cyan-100' : 'text-white hover:bg-white/10 hover:text-cyan-200'}`}
                                                >
                                                    {subItem.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Mobile Menu Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="lg:hidden text-white hover:text-cyan-400 transition-colors p-2"
                >
                    {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
                </button>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div className="absolute top-full left-0 right-0 bg-slate-900 border-t border-white/10 shadow-2xl lg:hidden overflow-y-auto max-h-[80vh]">
                        <div className="flex flex-col p-6 space-y-4">
                            {navLinks.map((link) => (
                                <div key={link.name} className="border-b border-white/5 last:border-0 pb-4 last:pb-0">
                                    <button
                                        onClick={() => {
                                            if (link.hasDropdown) {
                                                setActiveDropdown(activeDropdown === link.name ? null : link.name);
                                            } else {
                                                handleNavigation(link.target, link.isRoute);
                                                setIsMobileMenuOpen(false);
                                            }
                                        }}
                                        className="flex items-center justify-between w-full text-left text-lg font-bold text-white hover:text-cyan-400 transition-colors"
                                    >
                                        {link.name}
                                        {link.hasDropdown && (
                                            <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                                        )}
                                    </button>

                                    {/* Mobile Dropdown */}
                                    {link.hasDropdown && activeDropdown === link.name && (
                                        <div className="mt-2 pl-4 space-y-2 border-l-2 border-white/10 ml-2">
                                            {link.subItems.map((subItem) => (
                                                <button
                                                    key={subItem.name}
                                                    onClick={() => {
                                                        handleNavigation(subItem.target, subItem.isRoute);
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    className={`block w-full text-left py-2 text-sm font-medium transition-colors ${location.pathname === subItem.target ? 'text-cyan-400' : 'text-slate-300 hover:text-white'}`}
                                                >
                                                    {subItem.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}


            </div>
        </nav>
    );

};

export default Navbar;
